import Axhelper from '@/lib/Axhelper'

const SLOT_ORDER = {
	'8-14': 0,
	'14-20': 1,
}

export const AVAILABILITY_REQUESTS_CHANGED = 'availability-requests-changed'

const notifyAvailabilityRequestsChanged = () => {
	window.dispatchEvent(new Event(AVAILABILITY_REQUESTS_CHANGED))
}

const SORT_COLUMN_MAP = {
	dateFrom: 'startDate',
	dateTo: 'endDate',
	startDate: 'startDate',
	endDate: 'endDate',
	trainerName: 'userName',
	userName: 'userName',
	created: 'created',
	_id: '_id',
}

class AvailabilityService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			all: '/api/availability/all',
			details: '/api/availability/details',
			search: '/api/availability/search',
			filters: '/api/availability/filters',
			myAvailability: '/api/availability/myavailability',
			create: '/api/availability',
			update: '/api/availability',
			delete: '/api/availability',
			requests: '/api/availability/requests',
		}
	}

	async getAll(options = {}) {
		try {
			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
			} = options

			const params = new URLSearchParams({
				sortcol: this._mapSortColumn(sortColumn),
				sortdir: sortDirection,
				limit: String(limit),
			})
			const url = `${this.endpoints.all}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return this._emptyListResponse(response.error)
			}

			return this._buildListResponse(response)
		} catch (error) {
			console.error('AvailabilityService.getAll error:', error)
			return this._emptyListResponse(error.message || 'Failed to fetch availability')
		}
	}

	async getById(availabilityId) {
		try {
			if (!availabilityId) {
				return { data: null, error: 'Invalid availability ID provided' }
			}

			const url = `${this.endpoints.details}/${encodeURIComponent(availabilityId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return { data: null, error: response.error }
			}

			return {
				data: this._normalizeAvailability(response.data),
				error: null,
			}
		} catch (error) {
			console.error('AvailabilityService.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch availability details',
			}
		}
	}

	async search(searchTerm, options = {}) {
		try {
			if (!searchTerm) {
				return this._emptyListResponse('Invalid search term provided')
			}

			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
			} = options

			const params = new URLSearchParams({
				sortcol: this._mapSortColumn(sortColumn),
				sortdir: sortDirection,
				limit: String(limit),
			})
			const url = `${this.endpoints.search}/${encodeURIComponent(searchTerm)}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return this._emptyListResponse(response.error)
			}

			return this._buildListResponse(response)
		} catch (error) {
			console.error('AvailabilityService.search error:', error)
			return this._emptyListResponse(error.message || 'Failed to search availability')
		}
	}

	async getWithFilters(options = {}) {
		try {
			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = new URLSearchParams({
				sortcol: this._mapSortColumn(sortColumn),
				sortdir: sortDirection,
				limit: String(limit),
			})
			this._appendFilters(params, filters)
			const url = `${this.endpoints.filters}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return this._emptyListResponse(response.error)
			}

			return this._buildListResponse(response)
		} catch (error) {
			console.error('AvailabilityService.getWithFilters error:', error)
			return this._emptyListResponse(error.message || 'Failed to fetch availability')
		}
	}

	async getMyAvailability(options = {}) {
		try {
			const {
				startDate = null,
				endDate = null,
				start = null,
				end = null,
				limit = 50,
				sortColumn = 'startDate',
				sortDirection = 'asc',
			} = options

			const resolvedStartDate = startDate || start
			const resolvedEndDate = endDate || end

			if (!resolvedStartDate || !resolvedEndDate) {
				return this._emptyListResponse('startDate and endDate are required')
			}

			const params = new URLSearchParams({
				startDate: resolvedStartDate,
				endDate: resolvedEndDate,
				sortcol: this._mapSortColumn(sortColumn),
				sortdir: sortDirection,
				limit: String(limit),
			})
			const url = `${this.endpoints.myAvailability}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return this._emptyListResponse(response.error)
			}

			return this._buildListResponse(response)
		} catch (error) {
			console.error('AvailabilityService.getMyAvailability error:', error)
			return this._emptyListResponse(error.message || 'Failed to fetch my availability')
		}
	}

	async create(availabilityData = {}) {
		try {
			const formData = this._buildFormData(availabilityData)
			const validationError = this._validateFormData(formData)
			if (validationError) {
				return { success: false, error: validationError, data: null }
			}

			const response = await Axhelper.sendPostAsync(this.endpoints.create, formData)

			if (response.error) {
				return { success: false, error: response.error, data: null }
			}

			const payload = response.data
			return {
				success: true,
				error: null,
				data: Array.isArray(payload)
					? this._normalizeAvailabilities(payload)
					: this._normalizeAvailability(payload),
			}
		} catch (error) {
			console.error('AvailabilityService.create error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create availability',
				data: null,
			}
		}
	}

	async update(availabilityId, availabilityData = {}) {
		try {
			if (!availabilityId) {
				return { success: false, error: 'Invalid availability ID provided', data: null }
			}

			const formData = this._buildFormData(availabilityData)
			const validationError = this._validateFormData(formData)
			if (validationError) {
				return { success: false, error: validationError, data: null }
			}

			const url = `${this.endpoints.update}/${encodeURIComponent(availabilityId)}`
			const response = await Axhelper.sendPutAsync(url, formData)

			if (response.error) {
				return { success: false, error: response.error, data: null }
			}

			return {
				success: true,
				error: null,
				data: this._normalizeAvailability(response.data),
			}
		} catch (error) {
			console.error('AvailabilityService.update error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update availability',
				data: null,
			}
		}
	}

	async requestChange(availabilityId, type, availabilityData = {}) {
		try {
			if (!availabilityId) {
				return { success: false, error: 'Invalid availability ID provided', data: null }
			}

			const params = new URLSearchParams()
			params.append('availabilityId', availabilityId)
			params.append('type', type)
			if (type === 'edit') {
				const formData = this._buildFormData(availabilityData)
				const validationError = this._validateFormData(formData)
				if (validationError) {
					return { success: false, error: validationError, data: null }
				}
				formData.forEach((value, key) => params.append(key, value))
			}

			const response = await Axhelper.sendPostAsync(this.endpoints.requests, params)
			if (response.error) {
				return { success: false, error: response.error, data: null }
			}
			notifyAvailabilityRequestsChanged()
			return { success: true, error: null, data: response.data }
		} catch (error) {
			console.error('AvailabilityService.requestChange error:', error)
			return {
				success: false,
				error: error.message || 'Failed to send availability request',
				data: null,
			}
		}
	}

	async getRequests(options = {}) {
		try {
			const { limit = 200, sortColumn = 'created', sortDirection = 'desc' } = options
			const params = new URLSearchParams({
				sortcol: this._mapSortColumn(sortColumn),
				sortdir: sortDirection,
				limit: String(limit),
			})
			const response = await Axhelper.sendGetAsync(`${this.endpoints.requests}?${params.toString()}`)
			if (response.error) {
				return { data: [], error: response.error }
			}
			const rows = Array.isArray(response.data) ? response.data : []
			const headerCount = Number.parseInt(response.headers?.['x-total-count'], 10)
			return {
				data: rows,
				error: null,
				totalCount: Number.isFinite(headerCount) ? headerCount : rows.length,
			}
		} catch (error) {
			console.error('AvailabilityService.getRequests error:', error)
			return { data: [], error: error.message || 'Failed to load availability requests' }
		}
	}

	async acceptRequest(requestId) {
		return this._decideRequest(requestId, 'accept')
	}

	async rejectRequest(requestId) {
		return this._decideRequest(requestId, 'reject')
	}

	async _decideRequest(requestId, action) {
		try {
			if (!requestId) {
				return { success: false, error: 'Invalid request ID provided' }
			}
			const response = await Axhelper.sendPostAsync(
				`${this.endpoints.requests}/${encodeURIComponent(requestId)}/${action}`,
				new URLSearchParams(),
			)
			if (response.error) {
				return { success: false, error: response.error }
			}
			notifyAvailabilityRequestsChanged()
			return { success: true, error: null }
		} catch (error) {
			console.error(`AvailabilityService.${action}Request error:`, error)
			return { success: false, error: error.message || 'Failed to update availability request' }
		}
	}

	async delete(availabilityId) {
		try {
			if (!availabilityId) {
				return { success: false, error: 'Invalid availability ID provided' }
			}

			const url = `${this.endpoints.delete}/${encodeURIComponent(availabilityId)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return { success: false, error: response.error }
			}

			return { success: true, error: null }
		} catch (error) {
			console.error('AvailabilityService.delete error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete availability',
			}
		}
	}

	_appendFilters(params, filters = {}) {
		const {
			searchText = null,
			userId = null,
			trainerId = null,
			startDate = null,
			endDate = null,
			onDate = null,
			start = null,
			end = null,
			morning = null,
			afternoon = null,
		} = filters

		if (searchText) params.append('searchText', String(searchText))
		if (userId || trainerId) params.append('userId', String(userId || trainerId))
		if (onDate) params.append('onDate', String(onDate))
		if (startDate || start) params.append('startDate', String(startDate || start))
		if (endDate || end) params.append('endDate', String(endDate || end))
		if (morning !== null && morning !== undefined && morning !== '') {
			params.append('morning', String(morning))
		}
		if (afternoon !== null && afternoon !== undefined && afternoon !== '') {
			params.append('afternoon', String(afternoon))
		}
	}

	_buildFormData(data = {}) {
		const params = new URLSearchParams()
		const startDate = data.startDate || data.dateFrom || data.date || ''
		const endDate = data.endDate || data.dateTo || data.date || startDate

		let morning = data.morning
		let afternoon = data.afternoon

		if (morning === undefined && afternoon === undefined && Array.isArray(data.slots)) {
			const slotFlags = this._slotsToApiFlags(data.slots)
			morning = slotFlags.morning
			afternoon = slotFlags.afternoon
		}

		params.append('startDate', startDate)
		params.append('endDate', endDate)
		params.append('morning', String(Boolean(morning)))
		params.append('afternoon', String(Boolean(afternoon)))
		params.append('notes', data.notes || '')

		return params
	}

	_validateFormData(formData) {
		const startDate = formData.get('startDate')
		const endDate = formData.get('endDate')
		const morning = formData.get('morning') === 'true'
		const afternoon = formData.get('afternoon') === 'true'

		if (!startDate || !endDate) {
			return 'Invalid date range'
		}

		if (endDate < startDate) {
			return 'Invalid date range'
		}

		if (!morning && !afternoon) {
			return 'At least one slot is required'
		}

		return null
	}

	_buildListResponse(response) {
		const rows = this._normalizeAvailabilities(response.data)
		const totalCount = response.headers?.['x-total-count']
			? parseInt(response.headers['x-total-count'], 10)
			: rows.length

		return {
			data: rows,
			error: null,
			totalCount,
			hasMore: rows.length < totalCount,
			headers: response.headers,
		}
	}

	_emptyListResponse(error) {
		return {
			data: [],
			error,
			totalCount: 0,
			hasMore: false,
		}
	}

	_normalizeAvailabilities(items = []) {
		return Array.isArray(items)
			? items.map(item => this._normalizeAvailability(item)).filter(Boolean)
			: []
	}

	_normalizeAvailability(item) {
		if (!item) return null

		const startDate = item?.startDate || item?.dateFrom || item?.date || ''
		const endDate = item?.endDate || item?.dateTo || item?.date || startDate
		const slots = Array.isArray(item?.slots) && item.slots.length > 0
			? this._normalizeSlots(item.slots)
			: this._apiFlagsToSlots(item?.morning, item?.afternoon)

		return {
			...item,
			_id: item?._id || item?.id || '',
			startDate,
			endDate,
			dateFrom: startDate,
			dateTo: endDate,
			morning: this._toBoolean(item?.morning, slots.includes('8-14')),
			afternoon: this._toBoolean(item?.afternoon, slots.includes('14-20')),
			slots,
			userId: item?.userId || item?.trainerId || null,
			userName: item?.userName || item?.trainerName || '—',
			trainerId: item?.userId || item?.trainerId || null,
			trainerName: item?.userName || item?.trainerName || '—',
			notes: item?.notes || '',
			created: item?.created || '',
		}
	}

	_slotsToApiFlags(slots = []) {
		const normalizedSlots = this._normalizeSlots(slots)
		return {
			morning: normalizedSlots.includes('8-14'),
			afternoon: normalizedSlots.includes('14-20'),
		}
	}

	_apiFlagsToSlots(morning, afternoon) {
		const slots = []
		if (this._toBoolean(morning)) slots.push('8-14')
		if (this._toBoolean(afternoon)) slots.push('14-20')
		return this._normalizeSlots(slots)
	}

	_normalizeSlots(slots = []) {
		return (Array.isArray(slots) ? slots : [])
			.filter(Boolean)
			.sort((a, b) => (SLOT_ORDER[a] ?? 999) - (SLOT_ORDER[b] ?? 999))
	}

	_toBoolean(value, fallback = false) {
		if (value === true || value === 'true') return true
		if (value === false || value === 'false') return false
		return Boolean(fallback)
	}

	_mapSortColumn(sortColumn = '_id') {
		if (sortColumn === 'created') return 'created'
		return SORT_COLUMN_MAP[sortColumn] || sortColumn
	}
}

export default new AvailabilityService()
